import React, { useState, useEffect, useMemo, useRef } from "react";
import { Form, Button, Row, Col, Spinner, Alert, Card, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from 'helpers/api';
import { toast } from "react-toastify";
import ThemedSelect from "components/commonbreadcrumb/ThemedSelect";

const PRESET_MINUTES = [5, 10, 15, 30, 45, 60];

// Pre-dose and missed-dose alerts are sent 5–60 minutes from the dose.
const MIN_MINUTES = 5;
const MAX_MINUTES = 60;
const clampMinutes = (value) =>
  Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, Number(value) || MIN_MINUTES));

const MobileNotification = () => {
  const [facilities, setFacilities] = useState({});
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [remindersList, setRemindersList] = useState([]);
  const [selectedReminderId, setSelectedReminderId] = useState(null);
  const [showCustomDuration, setShowCustomDuration] = useState(false);

  // The facility picked last. Reminders that arrive for an earlier pick
  // (still loading when the user chose another facility) are ignored.
  const latestFacilityRef = useRef(null);

  useEffect(() => {
    const storedFacilities = JSON.parse(localStorage.getItem("facilitiesData"));
    if (storedFacilities) {
      setFacilities(storedFacilities);
      // Open on the first facility so its reminders show straight away.
      const firstFacility = Object.values(storedFacilities)[0];
      if (firstFacility) {
        handleFacilityChange({ value: firstFacility.value, label: firstFacility.key });
      }
    }
  }, []);

  const handleFacilityChange = async (selectedOption) => {
    latestFacilityRef.current = selectedOption.value;
    setSelectedFacility(selectedOption);
    setLoading(true);
    setError("");

    let tokenStr = localStorage.getItem("accessToken");

    try {
      const reminderRes = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/medpass/get-medpass-reminders/` + selectedOption.value,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );
      if (latestFacilityRef.current !== selectedOption.value) return;

      // A facility with no saved reminder settings gets the default timings
      // below, so nothing from the previously selected facility carries over.
      const reminderData = reminderRes.data?.reminder || {};
      const loadedReminders = reminderData.reminders;
      if (Array.isArray(loadedReminders) && loadedReminders.length > 0) {
        const mappedList = loadedReminders.map((rem) => ({
          id: rem.id,
          direction: rem.direction,
          ordinal: rem.ordinal,
          minutes_offset:
            rem.direction === "before" || rem.direction === "after"
              ? clampMinutes(rem.minutes_offset)
              : rem.minutes_offset ?? 0
        }));
        setRemindersList(mappedList);
        setSelectedReminderId(String(mappedList[0].id));
        setShowCustomDuration(
          !PRESET_MINUTES.includes(Number(mappedList[0].minutes_offset)) &&
          Number(mappedList[0].minutes_offset) !== 0
        );
      } else {
        // Legacy format, or no settings saved yet: default timings
        const mappedList = [
          {
            id: "reminder_1",
            direction: "before",
            ordinal: 1,
            minutes_offset: clampMinutes(Number(reminderData.reminder_1_duration) || 15)
          },
          {
            id: "reminder_2",
            direction: "at_dose",
            ordinal: 1,
            minutes_offset: Number(reminderData.reminder_2_duration) || 0
          },
          {
            id: "reminder_3",
            direction: "after",
            ordinal: 1,
            minutes_offset: clampMinutes(Number(reminderData.reminder_3_duration) || 15)
          }
        ];
        setRemindersList(mappedList);
        setSelectedReminderId("reminder_1");
        setShowCustomDuration(
          !PRESET_MINUTES.includes(Number(mappedList[0].minutes_offset)) &&
          Number(mappedList[0].minutes_offset) !== 0
        );
      }
    } catch (err) {
      if (latestFacilityRef.current !== selectedOption.value) return;
      setError("Error fetching reminders. Please try again.");
    } finally {
      if (latestFacilityRef.current === selectedOption.value) {
        setLoading(false);
      }
    }
  };

  const reminderOptions = useMemo(() => {
    if (!remindersList || remindersList.length === 0) {
      return [
        { value: "reminder_1", label: "Pre-Dose Alert" },
        { value: "reminder_2", label: "At-Dose Alert (On Time)" },
        { value: "reminder_3", label: "Missed-Dose Alert" }
      ];
    }

    const beforeCount = remindersList.filter((r) => r.direction === "before").length;
    const afterCount = remindersList.filter((r) => r.direction === "after").length;

    return remindersList.map((rem) => {
      let label = "";
      if (rem.direction === "before") {
        label = beforeCount > 1
          ? `Pre-Dose Alert ${rem.ordinal}`
          : "Pre-Dose Alert";
      } else if (rem.direction === "at_dose") {
        label = "At-Dose Alert (On Time)";
      } else if (rem.direction === "after") {
        label = afterCount > 1
          ? `Missed-Dose Alert ${rem.ordinal}`
          : "Missed-Dose Alert";
      } else {
        label = `Reminder ${rem.id}`;
      }

      return {
        value: String(rem.id),
        label: label
      };
    });
  }, [remindersList]);

  const activeReminder = useMemo(() => {
    return remindersList.find((r) => String(r.id) === String(selectedReminderId)) || remindersList[0] || null;
  }, [remindersList, selectedReminderId]);

  const handleReminderSelectChange = (option) => {
    if (!option) return;
    setSelectedReminderId(option.value);
    const target = remindersList.find((r) => String(r.id) === String(option.value));
    if (target) {
      setShowCustomDuration(
        !PRESET_MINUTES.includes(Number(target.minutes_offset)) &&
        Number(target.minutes_offset) !== 0
      );
    }
  };

  const handleDurationChange = (val) => {
    if (!activeReminder) return;
    const num = val === "" ? "" : Number(val);
    setRemindersList((prevList) =>
      prevList.map((rem) => {
        if (String(rem.id) !== String(activeReminder.id)) return rem;
        return { ...rem, minutes_offset: num };
      })
    );
  };

  const handleSaveSettings = async () => {
    const tokenStr = localStorage.getItem("accessToken");
    const facility_id = selectedFacility?.value;

    // Timing only: staff pick their own reminders in the mobile app. A real
    // reminder keeps its id so it is edited in place (subscribers kept); the
    // legacy placeholder ids ("reminder_1"…) are left off.
    const payload = {
      facility_id,
      reminders: remindersList.map((r) => ({
        ...(typeof r.id === "number" ? { id: r.id } : {}),
        direction: r.direction,
        minutes_offset:
          r.direction === "before" || r.direction === "after"
            ? clampMinutes(r.minutes_offset)
            : Number(r.minutes_offset) || 0
      }))
    };

    setSaving(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/medpass/update-medpass-reminders`,
        payload,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );
      toast.success(data.message || "Reminder settings saved successfully.");
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const beforeCount = remindersList.filter((r) => r.direction === "before").length;
  const afterCount = remindersList.filter((r) => r.direction === "after").length;

  return (
    <div className="p-3">
      <Row className="g-4">
        <Col>
          <div className="border">
            <Card.Header className="bg-light py-3 border-bottom">
              <h6 className="mb-0 fw-bold">
                <FontAwesomeIcon icon="bell" className="me-2 text-primary" />
                Mobile Notification Configuration (Per Facility)
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    Select Facility <span className="text-danger">*</span>
                  </Form.Label>
                  <ThemedSelect
                    options={Object.values(facilities).map((facility) => ({
                      value: facility.value,
                      label: facility.key
                    }))}
                    onChange={handleFacilityChange}
                    value={selectedFacility}
                    placeholder="Choose a Facility..."
                  />
                </Form.Group>

                {loading && (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" />
                    <p className="mt-2 text-secondary">Loading facility reminders...</p>
                  </div>
                )}

                {error && <Alert variant="danger">{error}</Alert>}

                {selectedFacility && !loading && !error && activeReminder && (
                  <>
                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Reminder Type</Form.Label>
                          <ThemedSelect
                            options={reminderOptions}
                            value={reminderOptions.find((opt) => opt.value === String(activeReminder.id))}
                            onChange={handleReminderSelectChange}
                            placeholder="Select Reminder Type"
                          />
                        </Form.Group>

                        {/* Duration section with Presets and Custom ±5min stepper */}
                        {(activeReminder.direction === "before" || activeReminder.direction === "after") && (
                          <div className="mb-4 pb-3 border-bottom">
                            <div className="d-flex justify-content-between align-items-baseline mb-2">
                              <Form.Label className="fw-semibold mb-0">
                                {activeReminder.direction === "before"
                                  ? `Pre-Dose Alert ${beforeCount > 1 ? activeReminder.ordinal : ""} Timing`
                                  : `Missed-Dose Alert ${afterCount > 1 ? activeReminder.ordinal : ""} Timing`}
                              </Form.Label>
                            </div>
                            <p className="text-muted small mb-2">
                              {activeReminder.direction === "before"
                                ? `Minutes before a scheduled dose to send the warning reminder (${MIN_MINUTES}–${MAX_MINUTES} min).`
                                : `Minutes after a scheduled dose to send the missed alert (${MIN_MINUTES}–${MAX_MINUTES} min).`}
                            </p>

                            {/* Custom Time Edit Field with ±5 min arrows */}
                            {true && (
                              <div className="mt-2 p-2 bg-light rounded border" style={{ maxWidth: "280px" }}>
                                <Form.Label className="small fw-semibold text-secondary mb-1">
                                  Minutes ({MIN_MINUTES}–{MAX_MINUTES} min):
                                </Form.Label>
                                <InputGroup size="sm">
                                  <Button
                                    variant="outline-secondary"
                                    onClick={() => {
                                      const current = Number(activeReminder.minutes_offset) || 0;
                                      const next = Math.max(MIN_MINUTES, current - 5);
                                      handleDurationChange(next);
                                    }}
                                    disabled={Number(activeReminder.minutes_offset) <= MIN_MINUTES}
                                    title="Decrease by 5 minutes"
                                  >
                                    <FontAwesomeIcon icon="minus" />
                                  </Button>
                                  <Form.Control
                                    type="number"
                                    min={MIN_MINUTES}
                                    max={MAX_MINUTES}
                                    step="5"
                                    className="text-center fw-bold"
                                    value={activeReminder.minutes_offset}
                                    onChange={(e) => handleDurationChange(e.target.value)}
                                    // A typed value outside 5–60 snaps back into range.
                                    onBlur={() => handleDurationChange(clampMinutes(activeReminder.minutes_offset))}
                                  />
                                  <Button
                                    variant="outline-secondary"
                                    onClick={() => {
                                      const current = Number(activeReminder.minutes_offset) || 0;
                                      const next = Math.min(MAX_MINUTES, current + 5);
                                      handleDurationChange(next);
                                    }}
                                    disabled={Number(activeReminder.minutes_offset) >= MAX_MINUTES}
                                    title="Increase by 5 minutes"
                                  >
                                    <FontAwesomeIcon icon="plus" />
                                  </Button>
                                  <InputGroup.Text>mins</InputGroup.Text>
                                </InputGroup>
                              </div>
                            )}
                          </div>
                        )}
                      </Col>
                    </Row>

                    <p className="text-muted small mb-0">
                      <FontAwesomeIcon icon="info-circle" className="me-1 text-info" />
                      Staff choose which reminders they receive, in the mobile app.
                    </p>

                    <div className="mt-4 text-end">
                      <Button
                        variant="primary"
                        className="px-4 fw-semibold"
                        onClick={handleSaveSettings}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Saving...
                          </>
                        ) : (
                          "Save Reminder Settings"
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </Form>
            </Card.Body>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default MobileNotification;
